import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MealPlan, MealPlanState, Meal, SavedMealPlanSummary } from '../types';
import {
    fetchHistory,
    fetchFavorites,
    saveFavorite,
    deleteFavorite,
    deleteHistory,
    streamMealPlan,
    chatRefinePlan,
    commitMealPlan,
    fetchMealPlanDetails,
    fetchFavoriteDetails,
    classifyIntent,
    patchMeal
} from '../services/api';
import { FavoriteRecipe, FavoriteRecipeDetail } from '../types';


export const isFavoriteDetail = (fav: FavoriteRecipe): fav is FavoriteRecipeDetail => {
    return fav.instructions !== null;
};


export type ActionType = 'GENERATE' | 'REFINE' | 'OFF_TOPIC';

export async function determineAction(
    token: string,
    command: string,
    planStatus: string | null | undefined,
    hasMeals: boolean
): Promise<ActionType> {
    // 1. Immutable State -> Always Generate
    if (planStatus === 'COMMITTED') {
        return 'GENERATE';
    }

    // 2. Empty Local State -> Always Generate
    if (!hasMeals) {
        return 'GENERATE';
    }

    // 3. Draft State (Ambiguous) -> Ask Backend
    // This is the CRITICAL safety layer replacing the brittle regex
    if (planStatus === 'DRAFT' || planStatus === 'completed') {
        try {
            const result = await classifyIntent(token, command);
            return result.intent;
        } catch (e) {
            console.warn("Intent classification failed, failing safe to REFINE:", e);
            return 'REFINE'; // Fail Safe: Assume refinement to avoid data loss
        }
    }

    // Fallback
    return 'REFINE';
}


interface MealPlanActions {
    setCurrentPlan: (plan: MealPlan | ((prev: MealPlan) => MealPlan)) => void;
    setChefMessage: (message: string | ((prev: string) => string)) => void;
    setSelectedMeal: (meal: { day: number | string, meal_type: string } | null) => void;
    loadHistory: (token: string) => Promise<void>;
    loadFavorites: (token: string) => Promise<void>;
    loadPlanDetails: (token: string, planId: string) => Promise<any>;
    loadFavoriteDetails: (token: string, favId: number) => Promise<any>;
    setRecentPlans: (plans: any[]) => void;
    setFavorites: (favorites: any[]) => void;
    handleToggleFavorite: (token: string, meal: Meal) => Promise<string | null>;
    handleDeleteHistory: (token: string, planId: number | string) => Promise<void>;
    handleCommitPlan: (token: string, planId: number | string, overrides?: any[], skipWineDeductions?: number[]) => Promise<void>;
    processCommand: (params: {
        token: string;
        command: string;
        mealSettings: any;
        onMacrosUpdate?: (update: any) => Promise<string | undefined>;
        onExclusionsUpdate?: (update: any) => Promise<string | undefined>;
        onInventoryUpdate?: (update: any) => void;
        onViewChange: (view: string) => void;
        onUpgradeRequired: (feature: string) => void;
        dietPhilosophy?: string | null;
        macroProfile?: string | null;
        householdSize?: number; // V13
    }) => Promise<void>;
    reset: () => void;
    cancelGeneration: () => void;
    toggleWineSelection: (token: string, meal: { day: number | string, meal_type: string, wine_pairing?: any }) => Promise<void>;
}

interface MealPlanStore extends MealPlanState, MealPlanActions { }

const initialState: MealPlanState = {
    currentPlan: {
        meals: [],
        shopping_list: [],
        status: 'DRAFT',
        title: 'Personalized Meal Plan',
        description: 'A balanced selection of meals.'
    },
    inventory: [],
    favorites: [],
    wine_cellar: [],
    recent_plans: [],
    selectedMeal: null,
    chefMessage: "",
    isLoading: false,
    detailLoadingRegistry: {},
    abortController: null,
    dirtyFields: new Set<string>(),
};

export const useMealPlanStore = create<MealPlanStore>()(
    devtools(
        (set, get) => ({
            ...initialState,

            reset: () => set(initialState),

            cancelGeneration: () => {
                const { abortController } = get();
                if (abortController) {
                    abortController.abort();
                    set({
                        abortController: null,
                        isLoading: false,
                        chefMessage: "Request cancelled."
                    });
                }
            },

            setCurrentPlan: (plan) => set((state) => ({
                currentPlan: typeof plan === 'function' ? plan(state.currentPlan) : plan
            })),

            setChefMessage: (message) => set((state) => ({
                chefMessage: typeof message === 'function' ? message(state.chefMessage) : message
            })),

            setSelectedMeal: (meal) => {
                if (!meal) {
                    set({ selectedMeal: null });
                } else {
                    set({ selectedMeal: { day: meal.day, meal_type: meal.meal_type } });
                }
            },

            toggleWineSelection: async (token, meal) => {
                const { currentPlan } = get();
                if (!currentPlan?.id) return;

                const originalMeal = currentPlan.meals.find((m: Meal) =>
                    String(m.day) === String(meal.day) && m.meal_type === meal.meal_type
                );

                if (!originalMeal) {
                    console.warn(`[STORE] toggleWineSelection: Meal not found for day ${meal.day} type ${meal.meal_type}`);
                    return;
                }

                const originalValue = originalMeal.wine_pairing?.selected !== false;
                const newValue = !originalValue;

                // 1. Mark field as dirty to protect from streaming clobber using INTERNAL helper or logic
                // Re-using the logic from markFieldDirty inline to ensure atomic update
                const dayStr = String(meal.day);
                const typeStr = meal.meal_type;
                const dirtyKey = `${dayStr}_${typeStr}_wine_pairing_selected`;

                set(state => {
                    const nextDirty = new Set(state.dirtyFields);
                    nextDirty.add(dirtyKey);
                    return { dirtyFields: nextDirty };
                });

                // 2. Optimistic Update using Robust Matching
                set(state => ({
                    currentPlan: {
                        ...state.currentPlan,
                        meals: state.currentPlan.meals.map(m =>
                            String(m.day) === String(meal.day) && m.meal_type === meal.meal_type
                                ? {
                                    ...m,
                                    wine_pairing: {
                                        ...m.wine_pairing,
                                        selected: newValue,
                                        name: m.wine_pairing?.name || '',
                                        reason: m.wine_pairing?.reason || '',
                                        shopping_list_add: m.wine_pairing?.shopping_list_add || false,
                                        is_varietal_suggestion: m.wine_pairing?.is_varietal_suggestion || false
                                    }
                                }
                                : m
                        )
                    }
                }));

                // 3. API Call & Rollback
                try {
                    await patchMeal(token, currentPlan.id, String(meal.day), meal.meal_type, {
                        wine_selection: newValue
                    });
                } catch (err) {
                    console.error("Wine toggle failed, rolling back:", err);
                    // Rollback
                    set(state => ({
                        currentPlan: {
                            ...state.currentPlan,
                            meals: state.currentPlan.meals.map(m =>
                                String(m.day) === String(meal.day) && m.meal_type === meal.meal_type
                                    ? {
                                        ...m,
                                        wine_pairing: {
                                            ...m.wine_pairing,
                                            selected: originalValue,
                                            name: m.wine_pairing?.name || '',
                                            reason: m.wine_pairing?.reason || '',
                                            shopping_list_add: m.wine_pairing?.shopping_list_add || false,
                                            is_varietal_suggestion: m.wine_pairing?.is_varietal_suggestion || false
                                        }
                                    }
                                    : m
                            )
                        }
                    }));

                    // Clear dirty bit to re-enable stream updates
                    set(state => {
                        const nextDirty = new Set(state.dirtyFields);
                        nextDirty.delete(dirtyKey);
                        return { dirtyFields: nextDirty };
                    });
                    // Re-throw if UI needs to show toast, or handle toast here if we invoke UI store?
                    // For now, let component handle toast or perform error suppression.
                    throw err;
                }
            },

            markFieldDirty: (day: number | string, mealType: string, field: string) => {
                const { dirtyFields } = get();
                // Ensure we use String() for consistency if called externally, but we are deprecating external use
                const key = `${day}_${mealType}_${field}`;
                const next = new Set(dirtyFields);
                next.add(key);
                set({ dirtyFields: next });
            },

            loadHistory: async (token) => {
                try {
                    const recent_plans = await fetchHistory(token);
                    set({ recent_plans });
                } catch (err) {
                    console.error("Failed to load recent_plans:", err);
                }
            },

            loadFavorites: async (token) => {
                try {
                    const favorites = await fetchFavorites(token);
                    set({ favorites });
                } catch (err) {
                    console.error("Failed to load favorites:", err);
                }
            },

            loadPlanDetails: async (token, planId) => {
                const { recent_plans } = get();
                const existing = recent_plans.find(h => h.id === planId);

                // If we already have meals, it's already full data
                if (existing?.plan_data?.meals && existing.plan_data.meals.length > 0) {
                    return existing;
                }

                set({ isLoading: true });
                try {
                    const fullPlan = await fetchMealPlanDetails(token, planId);
                    // Update recent_plans with full data so we don't fetch again
                    set(state => ({
                        recent_plans: state.recent_plans.map(h => h.id === planId ? fullPlan : h)
                    }));
                    return fullPlan;
                } catch (err) {
                    console.error("Failed to load plan details:", err);
                    throw err;
                } finally {
                    set({ isLoading: false });
                }
            },

            loadFavoriteDetails: async (token, favId: number) => {
                const { favorites, detailLoadingRegistry } = get();
                const existing = favorites.find(f => f.id === favId);

                // Check if already loading
                if (detailLoadingRegistry[favId]) {
                    return;
                }

                // If we already have instructions, it's already full data
                if (existing?.instructions !== null) {
                    return existing;
                }

                set(state => ({
                    detailLoadingRegistry: { ...state.detailLoadingRegistry, [favId]: true }
                }));

                try {
                    const fullFav = await fetchFavoriteDetails(token, favId);
                    // Update favorites with full data
                    set(state => ({
                        favorites: state.favorites.map(f => f.id === favId ? fullFav : f)
                    }));
                    return fullFav;
                } catch (err) {
                    console.error("Failed to load favorite details:", err);
                    throw err;
                } finally {
                    set(state => ({
                        detailLoadingRegistry: { ...state.detailLoadingRegistry, [favId]: false }
                    }));
                }
            },

            setRecentPlans: (recent_plans: SavedMealPlanSummary[]) => set({ recent_plans }),
            setFavorites: (newFavorites) => set((state) => {
                const merged = newFavorites.map(newFav => {
                    const existing = state.favorites.find(f => f.id === newFav.id);
                    // Anti-Demotion Merge Policy: 
                    // If local favorite has instructions (promoted), and incoming from server doesn't (summary),
                    // then preserve the local instructions.
                    if (existing && existing.instructions !== null && newFav.instructions === null) {
                        return { ...newFav, instructions: existing.instructions };
                    }
                    return newFav;
                });
                return { favorites: merged };
            }),

            handleToggleFavorite: async (token, meal) => {
                const { favorites } = get();
                // Check if already favorited (either by existing id or by name matching in favorites list)
                const existingFav = meal.id ? favorites.find(f => f.id === meal.id) : favorites.find(f => f.name === meal.name);

                try {
                    if (existingFav && existingFav.id != null) {
                        await deleteFavorite(token, existingFav.id);
                        set(state => ({
                            favorites: state.favorites.filter(f => f.id !== existingFav.id)
                        }));
                        return "Removed from favorites.";
                    } else {
                        await saveFavorite(token, {
                            name: meal.name,
                            instructions: meal.instructions || "",
                            macros: {
                                calories: meal.calories || 0,
                                protein: meal.protein || 0,
                                carbs: meal.carbs || 0,
                                fat: meal.fat || 0
                            }
                        });
                        const favs = await fetchFavorites(token);
                        set({ favorites: favs });
                        return "Added to favorites!";
                    }
                } catch (err) {
                    console.error("Favorite toggle failed:", err);
                    throw err;
                }
            },

            handleDeleteHistory: async (token, planId) => {
                try {
                    await deleteHistory(token, planId);
                    set(state => ({
                        recent_plans: state.recent_plans.filter(h => h.id !== planId)
                    }));
                } catch (err) {
                    console.error("Delete recent_plans failed:", err);
                    throw err;
                }
            },

            handleCommitPlan: async (token, planId, overrides = [], skipWineDeductions = []) => {
                try {
                    await commitMealPlan(token, planId, overrides, skipWineDeductions);
                    set((state) => ({
                        currentPlan: { ...state.currentPlan, status: 'COMMITTED' },
                        // Also update this plan in the recent_plans array so navigation preserves the state
                        recent_plans: state.recent_plans.map(h =>
                            h.id === planId
                                ? { ...h, status: 'COMMITTED', plan_data: { ...h.plan_data, status: 'COMMITTED' } }
                                : h
                        )
                    }));
                } catch (err) {
                    console.error("Commit plan failed:", err);
                    throw err;
                }
            },

            processCommand: async ({
                token,
                command,
                mealSettings,
                onMacrosUpdate,
                onExclusionsUpdate,
                onInventoryUpdate,
                onViewChange,
                onUpgradeRequired,
                dietPhilosophy,
                macroProfile,
                householdSize, // V13
            }) => {
                // Cancel any previous running request
                const { abortController: prevController } = get();
                if (prevController) {
                    prevController.abort();
                }

                const controller = new AbortController();
                set({ isLoading: true, chefMessage: "", abortController: controller });

                const { currentPlan } = get();

                try {
                    const isWineQuery = /\bwine\b|\bpair\b|\bpairing\b/i.test(command);

                    const action = await determineAction(
                        token,
                        command,
                        currentPlan?.status,
                        currentPlan?.meals?.length > 0
                    );

                    if (action === 'OFF_TOPIC') {
                        set({ chefMessage: "I'm sorry, I can only help with meal planning, recipes, and your inventory.", isLoading: false, abortController: null });
                        return;
                    }

                    if (action === 'GENERATE' && !isWineQuery) {
                        set({ chefMessage: "The Chef is preparing your custom menu. Each day's plan will be served as it's crafted." });
                        onViewChange('current');
                        set({
                            currentPlan: {
                                meals: [],
                                shopping_list: [],
                                status: 'DRAFT',
                                title: 'Personalized Meal Plan',
                                description: 'A balanced selection of meals.'
                            }
                        });
                        const enabledMealTypes = Object.entries(mealSettings).filter(([_, v]) => v).map(([k]) => k);
                        const { cachedContext } = (await import('./appContextStore')).useAppContextStore.getState();

                        const result = await streamMealPlan(token, command, enabledMealTypes, (partialData: any) => {
                            if (partialData.chef_message) {
                                set({ chefMessage: partialData.chef_message });
                            }
                            set((state) => {
                                let combinedMeals = partialData.meals
                                    ? [...partialData.meals]
                                    : [...state.currentPlan.meals];

                                // CONCURRENCY GUARD: Property-aware merge for dirty fields
                                const { dirtyFields } = get();
                                combinedMeals = combinedMeals.map(m => {
                                    const mealKey = `${m.day}_${m.meal_type}`;
                                    if (dirtyFields.has(`${mealKey}_wine_pairing_selected`)) {
                                        const originalMeal = state.currentPlan.meals.find(om =>
                                            String(om.day) === String(m.day) && om.meal_type === m.meal_type
                                        );
                                        if (originalMeal && originalMeal.wine_pairing) {
                                            console.warn(`[MERGE_GUARD]: Preserving local wine exclusion for ${mealKey}`);
                                            return {
                                                ...m,
                                                wine_pairing: {
                                                    ...m.wine_pairing,
                                                    selected: (originalMeal.wine_pairing as any).selected
                                                }
                                            };
                                        }
                                    }
                                    return m;
                                });

                                combinedMeals.sort((a, b) => {
                                    const getDayNum = (d: any) => {
                                        if (typeof d === 'number') return d;
                                        const match = String(d).match(/\d+/);
                                        return match ? parseInt(match[0], 10) : 999;
                                    };
                                    const dayA = getDayNum(a.day);
                                    const dayB = getDayNum(b.day);
                                    if (dayA !== dayB) return dayA - dayB;

                                    const priority: Record<string, number> = {
                                        "Breakfast": 1, "Lunch": 2, "Dinner": 3, "Snack": 4
                                    };
                                    const typeA = (a.meal_type || "").charAt(0).toUpperCase() + (a.meal_type || "").slice(1).toLowerCase();
                                    const typeB = (b.meal_type || "").charAt(0).toUpperCase() + (b.meal_type || "").slice(1).toLowerCase();
                                    return (priority[typeA] || 99) - (priority[typeB] || 99);
                                });

                                return {
                                    currentPlan: {
                                        ...state.currentPlan,
                                        ...partialData,
                                        meals: combinedMeals,
                                        shopping_list: partialData.shopping_list ? [...partialData.shopping_list] : state.currentPlan.shopping_list
                                    }
                                };
                            });
                        }, { dietPhilosophy, macroProfile, householdSize, cachedContext, signal: controller.signal });

                        set({
                            chefMessage: result?.chef_message || "Your meal plan is ready!",
                            currentPlan: { ...get().currentPlan, status: 'completed' }
                        });
                    } else {
                        set({ chefMessage: isWineQuery ? "Consulting the sommelier..." : "Adjusting your plan..." });

                        await chatRefinePlan(token, {
                            current_plan: currentPlan,
                            user_interaction: command,
                            diet_philosophy: dietPhilosophy,
                            macro_profile: macroProfile,
                            household_size: householdSize, // V13
                            signal: controller.signal
                        }, (partialData: any) => {
                            if (partialData.chef_message) {
                                set({ chefMessage: partialData.chef_message });
                            }

                            if (partialData.macro_update && onMacrosUpdate) {
                                onMacrosUpdate(partialData.macro_update).then(msg => {
                                    if (msg) set((state) => ({ chefMessage: `${state.chefMessage} ${msg}` }));
                                });
                            }

                            if (partialData.exclusion_update && onExclusionsUpdate) {
                                onExclusionsUpdate(partialData.exclusion_update).then(msg => {
                                    if (msg) set((state) => ({ chefMessage: `${state.chefMessage} ${msg}` }));
                                });
                            }

                            if (partialData.inventory_update && onInventoryUpdate) {
                                onInventoryUpdate(partialData.inventory_update);
                            }

                            if (partialData.meals || partialData.chef_message) {
                                set((state) => ({
                                    currentPlan: { ...state.currentPlan, ...partialData }
                                }));
                                onViewChange('current');
                            }
                        });
                    }
                } catch (err: any) {
                    if (err.name === 'AbortError') {
                        set({ isLoading: false, abortController: null });
                        return;
                    }
                    if (err.status === 402) {
                        onUpgradeRequired(err.message || "Meal Plans");
                    } else {
                        console.error("Command failed:", err);
                        set({ chefMessage: "I'm sorry, I couldn't process that command." });
                    }
                    throw err;
                } finally {
                    set({ isLoading: false, abortController: null, dirtyFields: new Set<string>() });
                }
            }
        }),
        { name: 'MealPlanStore' }
    )
);
